import { FormApi } from 'final-form';
import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { useCreateEditEventFlowStore } from '~admin/store/create-edit-event-flow-store-provider';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import { getOptionsForField } from '~admin/view/components/details/offer-group-details/offer-group-section-picker.helper';
import { SelectField } from '~admin/view/components/ui/form/fields/field-select.component';
import { Box } from '~common/components/box.component';
import { Button } from '~common/components/button/button.component';
import { Size } from '~common/components/button/button.helper';
import { isNilOrEmpty } from '~common/helper/global';
import { Color } from '~common/model/basics';
import { PlusIcon } from '~web/view/components/ui/icon/plus.icon';

interface Props {
  name: string;
  label: string;
  form?: FormApi<EventFormData>;
}

export const OfferGroupSectionPicker: React.FunctionComponent<Props> = observer(
  ({ name, label }) => {
    const { t } = useTranslation();
    const createEventFlowStore = useCreateEditEventFlowStore();
    const { availableSections, sectionsNotSelected } = createEventFlowStore;

    if (isNilOrEmpty(availableSections)) {
      return null;
    }

    return (
      <>
        <FieldArray name={`${name}.sections`}>
          {({ fields }): ReactNode => {
            const showRemoveButton = fields.length > 1;
            const addButtonDisabled =
              sectionsNotSelected.length === 0 ||
              fields.length === availableSections.length ||
              isNilOrEmpty(fields.value[fields.length - 1]);

            const onRemove = (): void => {
              fields.pop();
            };

            const onAdd = (): void => {
              fields.push(sectionsNotSelected[0].code);
            };

            return (
              <>
                {fields.map((fieldName, index) => {
                  const sectionList = getOptionsForField(availableSections);
                  return (
                    <RestrictBox key={index}>
                      <SelectField
                        name={fieldName}
                        label={label}
                        options={sectionList}
                        disabled={isNilOrEmpty(sectionList) || index < fields.length - 1}
                      />
                    </RestrictBox>
                  );
                })}
                <ButtonContainer showRemoveButton={showRemoveButton}>
                  {showRemoveButton && (
                    <Button color={Color.Gray} size={Size.Small} onClick={onRemove}>
                      {t('form.label.remove')}
                    </Button>
                  )}
                  <Button
                    color={Color.Gray}
                    size={Size.Small}
                    disabled={addButtonDisabled}
                    onClick={onAdd}
                  >
                    <StyledPlusIcon width={12} height={12} />
                    {t('createEvent.offerGroupDetails.addSection')}
                  </Button>
                </ButtonContainer>
              </>
            );
          }}
        </FieldArray>
      </>
    );
  },
);

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin: 0 12px;
`;

const ButtonContainer = styled(({ showRemoveButton, ...rest }) => <Box {...rest} />)<{
  showRemoveButton: boolean;
}>`
  margin: 16px 12px 0;
  ${(props): FlattenSimpleInterpolation =>
    props.showRemoveButton
      ? css`
          justify-content: space-between;
        `
      : css`
          justify-content: flex-end;
        `};
`;

const StyledPlusIcon = styled(PlusIcon)`
  fill: currentColor;
  margin-right: 4px;
`;



// WEBPACK FOOTER //
// ./view/components/details/offer-group-details/offer-group-section-picker.component.tsx